import React, { useState, useEffect } from 'react';
import { Game as GameType, Board } from '@/types/game';
import { getValidMoves, isValidMove, makeMove, checkGameOver, calculateScores } from '@/utils/gameLogic';
import { api } from '@/utils/api';
import { GameBoard } from './GameBoard';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSelector } from './LanguageSelector';

interface GameProps {
    sessionId: string;
    gameId: string;
    onNavigateToLobby: () => void;
    showModal: (message: string) => void;
}

// 相手の最後の手を検出する関数
const detectOpponentMove = (prevBoard: Board, newBoard: Board, myPlayerNumber: 1 | 2): [number, number] | null => {
    // ボードの変化を比較して、新しく置かれた石を検出
    for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
            // 空のセルから相手の石になった場合、それが相手の手
            if (prevBoard[row][col] === 0 && newBoard[row][col] !== 0 && newBoard[row][col] !== myPlayerNumber) {
                return [row, col];
            }
        }
    }
    return null;
};

export const Game: React.FC<GameProps> = ({
    sessionId,
    gameId,
    onNavigateToLobby,
    showModal
}) => {
    const { t } = useLanguage();
    const [game, setGame] = useState<GameType | null>(null);
    const [loading, setLoading] = useState(true);
    const [playerNumber, setPlayerNumber] = useState<1 | 2 | null>(null);
    const [validMoves, setValidMoves] = useState<[number, number][]>([]);
    const [flippingPieces, setFlippingPieces] = useState<[number, number][]>([]);
    const [lastMovePosition, setLastMovePosition] = useState<[number, number] | null>(null);
    const [gameEndAnimation, setGameEndAnimation] = useState(false);
    const [previewFlips, setPreviewFlips] = useState<[number, number][]>([]);
    const [isOptimisticUpdate, setIsOptimisticUpdate] = useState(false);
    const [waitForNextTurn, setWaitForNextTurn] = useState(false);
    const [previousBoard, setPreviousBoard] = useState<Board | null>(null);

    useEffect(() => {
        if (!gameId || !sessionId) return;

        const fetchGame = async () => {
            try {
                const response = await api.getGame(gameId);

                if (response.success) {
                    const gameData = response.data;

                    // 楽観的更新中でない場合のみゲーム状態を更新
                    if (!isOptimisticUpdate) {
                        // プレイヤー番号を最初に判定
                        let currentPlayerNumber: 1 | 2 | null = null;
                        if (gameData.player1_id === sessionId) {
                            currentPlayerNumber = 1;
                            setPlayerNumber(1);
                        } else if (gameData.player2_id === sessionId) {
                            currentPlayerNumber = 2;
                            setPlayerNumber(2);
                        } else {
                            setPlayerNumber(null);
                        }

                        // 相手の最後の手を検出（previousBoardが存在し、自分がプレイヤーの場合のみ）
                        if (currentPlayerNumber && previousBoard && gameData.board) {
                            const opponentMove = detectOpponentMove(previousBoard, gameData.board, currentPlayerNumber);
                            if (opponentMove) {
                                setLastMovePosition(opponentMove);
                            }
                        }

                        setGame(gameData);
                        setPreviousBoard(gameData.board);
                    }
                    setLoading(false);

                    // プレイヤー番号を判定（楽観的更新中の場合も必要）
                    let currentPlayerNumber: 1 | 2 | null = null;
                    if (gameData.player1_id === sessionId) {
                        currentPlayerNumber = 1;
                        if (!isOptimisticUpdate) setPlayerNumber(1);
                    } else if (gameData.player2_id === sessionId) {
                        currentPlayerNumber = 2;
                        if (!isOptimisticUpdate) setPlayerNumber(2);
                    } else {
                        if (!isOptimisticUpdate) setPlayerNumber(null);
                    }

                    // 楽観的更新中でない場合のみ有効手を計算
                    if (!isOptimisticUpdate) {
                        if (gameData.status === 'playing' && gameData.current_player === currentPlayerNumber) {
                            const moves = getValidMoves(gameData.board, currentPlayerNumber);
                            setValidMoves(moves);
                        } else {
                            setValidMoves([]);
                        }
                    }

                    // ゲーム終了時の演出（楽観的更新中でない場合のみ）
                    if (!isOptimisticUpdate && gameData.status === 'finished' && !gameEndAnimation) {
                        setGameEndAnimation(true);
                        setTimeout(() => setGameEndAnimation(false), 3000);
                    }

                    // ValidMove表示制御
                    if (!isOptimisticUpdate) {
                        // 自分のターンで、かつ次のターン待機フラグが立っている場合のみ表示
                        if (
                            gameData.status === 'playing' &&
                            gameData.current_player === currentPlayerNumber &&
                            waitForNextTurn
                        ) {
                            const moves = getValidMoves(gameData.board, currentPlayerNumber);
                            setValidMoves(moves);
                            setWaitForNextTurn(false);
                        } else if (gameData.current_player !== currentPlayerNumber) {
                            // 自分のターンでなければクリア
                            setValidMoves([]);
                        }
                    }
                } else {
                    throw new Error(response.error || t('error.fetchFailed'));
                }
            } catch (error) {
                console.error('ゲーム取得エラー:', error);
                showModal(t('error.fetchFailed'));
                onNavigateToLobby();
            }
        };

        fetchGame();

        // 定期的にゲーム状態を更新
        const interval = setInterval(fetchGame, 2000);
        return () => clearInterval(interval);
    }, [gameId, sessionId, onNavigateToLobby, showModal, t, isOptimisticUpdate, waitForNextTurn]);

    const handleCellClick = async (row: number, col: number) => {
        if (!game || game.status !== 'playing' || game.current_player !== playerNumber || !playerNumber) {
            showModal(t('error.notYourTurn'));
            return;
        }

        if (!isValidMove(game.board, row, col, playerNumber)) {
            showModal(t('error.invalidMove'));
            return;
        }

        // 次のターン確定までValidMoveを非表示
        setWaitForNextTurn(true);
        setIsOptimisticUpdate(true);
        setValidMoves([]);

        // 自分が石を置く時は赤枠を非表示
        setLastMovePosition(null);

        // 楽観的更新: 即座に画面の状態を更新
        const oldBoard = game.board;
        const newBoard = makeMove(game.board, row, col, playerNumber);
        const nextPlayer = (playerNumber === 1 ? 2 : 1) as 1 | 2;

        // ゲーム終了判定
        const isGameOver = checkGameOver(newBoard);
        let finalNextPlayer = nextPlayer;
        let gameStatus: 'waiting' | 'playing' | 'finished' = game.status;
        let winner = game.winner;

        if (isGameOver) {
            const scores = calculateScores(newBoard);
            gameStatus = 'finished';
            if (scores.black > scores.white) winner = 1;
            else if (scores.white > scores.black) winner = 2;
            else winner = null;
        } else {
            // 次のプレイヤーが打てるかチェック
            const nextPlayerMoves = getValidMoves(newBoard, nextPlayer);
            if (nextPlayerMoves.length === 0) {
                // 次のプレイヤーが打てない場合、ターンをスキップ
                const afterNextPlayer = (nextPlayer === 1 ? 2 : 1) as 1 | 2;
                const afterNextPlayerMoves = getValidMoves(newBoard, afterNextPlayer);
                if (afterNextPlayerMoves.length === 0) {
                    // 両プレイヤーが打てない場合、ゲーム終了
                    const scores = calculateScores(newBoard);
                    gameStatus = 'finished';
                    if (scores.black > scores.white) winner = 1;
                    else if (scores.white > scores.black) winner = 2;
                    else winner = null;
                } else {
                    finalNextPlayer = afterNextPlayer;
                }
            }
        }

        // アニメーション用のピース特定
        const flipped: [number, number][] = [];
        for (let r = 0; r < 8; r++) {
            for (let c = 0; c < 8; c++) {
                if (oldBoard[r][c] !== newBoard[r][c] && newBoard[r][c] === playerNumber) {
                    flipped.push([r, c]);
                }
            }
        }

        // 即座に画面を更新（楽観的更新）
        const updatedGame = {
            ...game,
            board: newBoard,
            current_player: finalNextPlayer,
            status: gameStatus,
            winner: winner
        };
        setGame(updatedGame);
        setFlippingPieces(flipped);
        setLastMovePosition(null);

        // アニメーション後の処理
        let serverConfirmed = false;
        let animationFinished = false;

        const finishOptimisticUpdateIfReady = () => {
            if (serverConfirmed && animationFinished) {
                setIsOptimisticUpdate(false);
            }
        };

        const animationTimeout = setTimeout(() => {
            setFlippingPieces([]);
            setValidMoves([]); // 確実にクリア
            animationFinished = true;
            finishOptimisticUpdateIfReady();
        }, 400);

        // バックグラウンドでサーバーに送信
        try {
            const response = await api.makeMove(gameId, sessionId, row, col);
            if (!response.success) {
                throw new Error(response.error || t('error.moveFailed'));
            }
            serverConfirmed = true;
            finishOptimisticUpdateIfReady();
        } catch (error) {
            console.error('手を打つエラー:', error);
            // エラー時はアニメーションタイムアウトをクリア
            clearTimeout(animationTimeout);

            // エラー時は楽観的更新を即座に終了して元の状態に戻す
            setIsOptimisticUpdate(false);
            setWaitForNextTurn(false);
            setGame(game);
            setFlippingPieces([]);
            setLastMovePosition(null);
            setValidMoves(getValidMoves(game.board, playerNumber!));
            showModal(t('error.moveFailed'));

            // エラー時も少し遅延させて有効手を再表示（UXを向上）
            setTimeout(() => {
                if (game.current_player === playerNumber) {
                    const moves = getValidMoves(game.board, playerNumber);
                    setValidMoves(moves);
                }
            }, 100); // 短い遅延で自然な表示
        }
    };

    const handleCellRightClick = (row: number, col: number) => {
        if (!game || game.status !== 'playing' || game.current_player !== playerNumber || !playerNumber) {
            return;
        }

        if (!isValidMove(game.board, row, col, playerNumber)) {
            return;
        }

        // この手を打った場合にひっくり返る石を計算
        const newBoard = makeMove(game.board, row, col, playerNumber);
        const flips: [number, number][] = [];
        for (let r = 0; r < 8; r++) {
            for (let c = 0; c < 8; c++) {
                if (game.board[r][c] !== newBoard[r][c] && newBoard[r][c] === playerNumber && !(r === row && c === col)) {
                    flips.push([r, c]);
                }
            }
        }
        setPreviewFlips(flips);

        // 2秒後にプレビューを消す
        setTimeout(() => setPreviewFlips([]), 2000);
    };

    const leaveGame = async () => {
        try {
            const response = await api.leaveGame(gameId, sessionId);
            if (response.success) {
                onNavigateToLobby();
            } else {
                throw new Error(response.error || t('error.leaveFailed'));
            }
        } catch (error) {
            console.error('ゲーム離脱エラー:', error);
            showModal(t('error.leaveFailed'));
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="absolute top-4 right-4">
                    <LanguageSelector />
                </div>
                <p className="text-lg font-semibold text-slate-700">{t('game.loading')}</p>
            </div>
        );
    }

    if (!game) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="absolute top-4 right-4">
                    <LanguageSelector />
                </div>
                <div className="text-center">
                    <p className="text-lg font-semibold text-slate-700 mb-4">{t('game.noGameData')}</p>
                    <button
                        onClick={onNavigateToLobby}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition"
                    >
                        {t('game.backToLobby')}
                    </button>
                </div>
            </div>
        );
    }

    const isMyTurn = game.current_player === playerNumber;
    const player1Status = game.player1_id ? t('player.player1') : t('player.notJoined');
    const player2Status = game.player2_id ? t('player.player2') : t('player.notJoined');

    let gameStatusMessage = '';
    if (game.status === 'waiting') {
        gameStatusMessage = t('status.waitingForPlayer');
    } else if (game.status === 'playing') {
        gameStatusMessage = isMyTurn ? t('status.yourTurn') : (game.current_player === 1 ? t('status.blackTurn') : t('status.whiteTurn'));
    } else if (game.status === 'finished') {
        // スコアを計算
        let blackScore = 0, whiteScore = 0;
        for (let row = 0; row < 8; row++) {
            for (let col = 0; col < 8; col++) {
                if (game.board[row][col] === 1) blackScore++;
                else if (game.board[row][col] === 2) whiteScore++;
            }
        }

        if (blackScore === whiteScore) {
            gameStatusMessage = `🎊 ${t('status.gameFinished')} ${t('status.draw')} 🎊\n${t('game.black')}: ${blackScore} - ${t('game.white')}: ${whiteScore}`;
        } else if (blackScore > whiteScore) {
            const isMyWin = playerNumber === 1;
            gameStatusMessage = `🎉 ${t('status.gameFinished')} ${t('status.blackWins')} 🎉\n${t('game.black')}: ${blackScore} - ${t('game.white')}: ${whiteScore}\n${isMyWin ? `🏆 ${t('status.congratulations')} 🏆` : `😔 ${t('status.betterLuckNextTime')} 😔`}`;
        } else {
            const isMyWin = playerNumber === 2;
            gameStatusMessage = `🎉 ${t('status.gameFinished')} ${t('status.whiteWins')} 🎉\n${t('game.white')}: ${whiteScore} - ${t('game.black')}: ${blackScore}\n${isMyWin ? `🏆 ${t('status.congratulations')} 🏆` : `😔 ${t('status.betterLuckNextTime')} 😔`}`;
        }
    }

    // スコアを計算
    let blackScore = 0, whiteScore = 0;
    for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
            if (game.board[row][col] === 1) blackScore++;
            else if (game.board[row][col] === 2) whiteScore++;
        }
    }

    return (
        <div className="relative min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="absolute top-4 right-4">
                <LanguageSelector />
            </div>
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg w-full max-w-4xl text-center flex flex-col items-center">
                <h2 className="text-3xl font-bold mb-4 text-slate-900">{t('game.title')}</h2>
                <p className="text-slate-700 mb-2">
                    {t('game.gameId')}: <span className="font-mono text-sm break-all">{gameId}</span>
                </p>
                <p className="text-slate-700 mb-4">
                    {t('game.yourColor')}: <span className={`font-semibold ${playerNumber === 1 ? 'text-gray-900' : 'text-gray-300'}`}>
                        {playerNumber === 1 ? t('game.black') : playerNumber === 2 ? t('game.white') : t('game.spectator')}
                    </span>
                </p>

                <div className="flex flex-col items-center w-full">
                    {/* 黒プレイヤー情報 */}
                    <div className="w-full p-4 bg-slate-100 rounded-lg shadow-inner mb-4">
                        <h3 className="text-xl font-bold mb-3">{t('player.blackPlayer')}</h3>
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-lg">{t('player.score')}:</span>
                            <span className="text-xl font-bold">{blackScore}</span>
                        </div>
                        <p className="text-sm text-slate-600">{player1Status}</p>
                    </div>

                    {/* ゲーム状態 */}
                    <div className="w-full p-4 bg-slate-100 rounded-lg shadow-inner mb-4">
                        <h3 className="text-xl font-bold mb-3">{t('status.gameState')}</h3>
                        <div className={`text-xl font-semibold ${isMyTurn ? 'text-blue-600' : 'text-slate-800'}`}>
                            {gameStatusMessage.split('\n').map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </div>
                    </div>

                    {/* ゲームボード */}
                    {game.board && Array.isArray(game.board) && game.board.length === 8 ? (
                        <div className="mb-4">
                            <GameBoard
                                board={game.board}
                                validMoves={validMoves}
                                flippingPieces={flippingPieces}
                                onCellClick={handleCellClick}
                                onCellRightClick={handleCellRightClick}
                                lastMovePosition={lastMovePosition}
                                previewFlips={previewFlips}
                                gameEndAnimation={gameEndAnimation}
                            />
                        </div>
                    ) : (
                        <div className="mb-4 w-96 h-96 bg-gray-200 flex items-center justify-center rounded-lg">
                            <p className="text-gray-600">{t('common.loading')}</p>
                        </div>
                    )}

                    {/* 白プレイヤー情報 */}
                    <div className="w-full p-4 bg-slate-100 rounded-lg shadow-inner mb-4">
                        <h3 className="text-xl font-bold mb-3">{t('player.whitePlayer')}</h3>
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-lg">{t('player.score')}:</span>
                            <span className="text-xl font-bold">{whiteScore}</span>
                        </div>
                        <p className="text-sm text-slate-600">{player2Status}</p>
                    </div>

                    {/* ボタン */}
                    <div className="flex space-x-4">
                        <button
                            onClick={onNavigateToLobby}
                            className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition"
                        >
                            {t('game.backToLobby')}
                        </button>
                        {/* 観戦モード（playerNumber が null）の場合のみ Leave Game ボタンを表示 */}
                        {playerNumber === null && (
                            <button
                                onClick={leaveGame}
                                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition"
                            >
                                {t('game.leaveGame')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}; 
